import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  PointerSensor,
  closestCorners,
  defaultDropAnimationSideEffects,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Flag } from "lucide-react";
import * as React from "react";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";

import {
  KANBAN_DROP_PLACEHOLDER_ATTR,
  VideoProjectKanbanBoard,
  VideoProjectKanbanColumnDropState,
  isKanbanColumnFetchAborted,
} from "@/components/VideoProjectKanban";
import {
  assignedUserIdForRole,
  assigningRoleForEvent,
  canUnassignStaff,
  confirmAssignSelfOnTransition,
} from "@/components/VideoProjectStaffPicker";
import { getValidStatusColumnIds, getVideoProjectEventForStatusChange } from "@/components/VideoProjectStatusPicker";
import { Badge, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";
import { User, VideoProject, VideoProjectServices } from "@/services/bedrock";
import { editorVideoProjectDetailUrl, getErrorMessage, useSocialCoachSession } from "@/utils";

import { KanbanCard, KanbanCardOverlay } from "./KanbanCard";
import {
  KanbanColumnDef,
  KanbanColumnId,
  getKanbanColumnId,
  getRepresentativeStatusForColumn,
  isKanbanCardDraggable,
  visibleKanbanColumns,
} from "./kanbanColumns";
import {
  KanbanBoardData,
  KanbanBoardFilters,
  KANBAN_EXTRA_FIELDS,
  appendKanbanColumnPage,
  applyKanbanVideoProject,
  columnIdForStatus,
  fetchKanbanBoard,
  fetchKanbanVideoProject,
  fetchNextKanbanColumnPage,
  kanbanBoardQueryKey,
  moveProjectInKanbanBoardData,
  refreshKanbanColumns,
  removeProjectFromKanbanBoardData,
  reuseKanbanBoardData,
  reuseKanbanProjectList,
  setKanbanBoardQueryData,
  updateProjectInKanbanBoardData,
} from "./kanbanColumnQuery";
import {
  columnIdsForEditorQueueEvent,
  EDITOR_QUEUE_VIDEO_PROJECT_CHANGED,
  EditorQueueVideoProjectChanged,
} from "./editorQueueEvents";
import { buildOptimisticKanbanProjectPatch, insertProjectInKanbanQueueOrder } from "./kanbanQueueOrder";
import { useEditorQueueCable } from "./useEditorQueueCable";

const EMPTY_PROJECTS: VideoProject[] = [];
const DROP_ANIMATION_MS = 280;
const DROP_EASING = "cubic-bezier(0.2, 0, 0, 1)";
const KANBAN_PROJECT_ID_ATTR = "data-kanban-project-id";

function isQaResolutionEvent(event: VideoProject.Event) {
  return event === VideoProject.Event.REJECT_QA || event === VideoProject.Event.APPROVE_QA;
}

const DROP_ANIMATION: DropAnimation = {
  duration: DROP_ANIMATION_MS,
  easing: DROP_EASING,
  keyframes: ({ transform: { initial, final }, dragOverlay }) => {
    const destRect = document.querySelector(`[${KANBAN_DROP_PLACEHOLDER_ATTR}]`)?.getBoundingClientRect();
    const overlayRect = dragOverlay.rect;
    const to = destRect
      ? {
          ...initial,
          x: initial.x + (destRect.left - overlayRect.left),
          y: initial.y + (destRect.top - overlayRect.top),
          scaleX: 1,
          scaleY: 1,
        }
      : final;
    return [
      { transform: CSS.Transform.toString(initial), opacity: 0.8 },
      { transform: CSS.Transform.toString(to), opacity: 1 },
    ];
  },
  sideEffects: args => {
    const cleanup = defaultDropAnimationSideEffects({
      styles: { active: { opacity: "0" } },
    })(args);

    const overlayCard = args.dragOverlay.node.firstElementChild as HTMLElement | null;
    if (overlayCard) {
      overlayCard.style.transition = `transform ${DROP_ANIMATION_MS}ms ${DROP_EASING}, opacity ${DROP_ANIMATION_MS}ms ${DROP_EASING}`;
      overlayCard.style.transform = "rotate(0deg) scale(1)";
      overlayCard.style.opacity = "1";
    }

    return cleanup;
  },
};

class KanbanPointerSensor extends PointerSensor {
  static activators: typeof PointerSensor.activators = [
    {
      eventName: "onPointerDown" as const,
      handler: (event, options) => {
        const nativeEvent = event.nativeEvent;
        if (nativeEvent.target instanceof Element && nativeEvent.target.closest("[data-kanban-interactive]")) {
          return false;
        }
        return PointerSensor.activators[0].handler(event, options);
      },
    },
  ];
}

interface ColumnSnapshot {
  projects: VideoProject[];
  totalCount: number;
  totalPriorityCount: number;
}

interface BoardMutationContext {
  previousBoardData?: KanbanBoardData;
  previousSnapshots?: Partial<Record<KanbanColumnId, ColumnSnapshot>>;
}

function boardDataToSnapshots(
  boardData: KanbanBoardData,
  columns: KanbanColumnDef[],
  previous?: Partial<Record<KanbanColumnId, ColumnSnapshot>>
): Record<KanbanColumnId, ColumnSnapshot> {
  return Object.fromEntries(
    columns.map(column => {
      const columnData = boardData[column.id];
      const projects = reuseKanbanProjectList(previous?.[column.id]?.projects, columnData?.projects ?? []);
      const totalCount = columnData?.totalCount ?? 0;
      const totalPriorityCount = columnData?.totalPriorityCount ?? 0;
      const previousSnapshot = previous?.[column.id];
      if (
        previousSnapshot &&
        previousSnapshot.projects === projects &&
        previousSnapshot.totalCount === totalCount &&
        previousSnapshot.totalPriorityCount === totalPriorityCount
      ) {
        return [column.id, previousSnapshot];
      }
      return [column.id, { projects, totalCount, totalPriorityCount }];
    })
  ) as Record<KanbanColumnId, ColumnSnapshot>;
}

function reuseColumnSnapshots(
  previous: Partial<Record<KanbanColumnId, ColumnSnapshot>>,
  next: Record<KanbanColumnId, ColumnSnapshot>,
  columns: KanbanColumnDef[]
): Partial<Record<KanbanColumnId, ColumnSnapshot>> {
  if (
    columns.length === Object.keys(previous).length &&
    columns.every(column => previous[column.id] === next[column.id])
  ) {
    return previous;
  }
  return next;
}

function snapshotsFromBoardData(
  boardData: KanbanBoardData,
  columns: KanbanColumnDef[],
  previous?: Partial<Record<KanbanColumnId, ColumnSnapshot>>
): Partial<Record<KanbanColumnId, ColumnSnapshot>> {
  return reuseColumnSnapshots(previous ?? {}, boardDataToSnapshots(boardData, columns, previous), columns);
}

function findContainer(
  cardId: string,
  snapshots: Partial<Record<KanbanColumnId, ColumnSnapshot>>
): KanbanColumnId | null {
  if (snapshots[cardId as KanbanColumnId]) {
    return cardId as KanbanColumnId;
  }

  for (const columnId of Object.keys(snapshots) as KanbanColumnId[]) {
    if ((snapshots[columnId]?.projects ?? []).some(project => String(project.id) === cardId)) {
      return columnId;
    }
  }

  return null;
}

function getKanbanDropPlaceholderIndex({
  startSnapshots,
  project,
  sourceColumnId,
  hoverColumnId,
  validDropColumnIds,
  currentUserId,
}: {
  startSnapshots: Partial<Record<KanbanColumnId, ColumnSnapshot>>;
  project: VideoProject;
  sourceColumnId: KanbanColumnId;
  hoverColumnId: KanbanColumnId | null;
  validDropColumnIds: KanbanColumnId[];
  currentUserId?: number;
}): number | null {
  if (!hoverColumnId || hoverColumnId === sourceColumnId || !validDropColumnIds.includes(hoverColumnId)) {
    return null;
  }

  const dropRequest = getVideoProjectEventForStatusChange(project.status, hoverColumnId, {
    requiresQa: project.requiresQa,
    currentUserId,
  });
  if (!dropRequest) return null;

  const toColumn = startSnapshots[hoverColumnId];
  if (!toColumn) return null;

  const viewerId = currentUserId ?? 0;
  const role = assigningRoleForEvent(dropRequest.event);
  const existingAssigneeId = role ? assignedUserIdForRole(project, role) : null;
  const actorId = existingAssigneeId ?? dropRequest.userId ?? viewerId;
  const patchedProject = {
    ...project,
    ...buildOptimisticKanbanProjectPatch(project, {
      status: getRepresentativeStatusForColumn(hoverColumnId),
      event: dropRequest.event,
      currentUserId: viewerId,
      assignedUserId: actorId,
    }),
  };
  const destWithout = toColumn.projects.filter(item => item.id !== project.id);
  const insertIndex = insertProjectInKanbanQueueOrder(destWithout, patchedProject, hoverColumnId, viewerId).findIndex(
    item => item.id === project.id
  );

  return insertIndex === -1 ? destWithout.length : insertIndex;
}

const GENERIC_ERROR_MESSAGE = "An unexpected error occurred.";

function formatKanbanTransitionError(error: unknown): React.ReactNode {
  const message = getErrorMessage(error);
  if (!message || message === GENERIC_ERROR_MESSAGE) {
    return undefined;
  }

  const details = message
    .split(/\s*;\s*/)
    .map(part => part.trim())
    .filter(part => part && !/^Event '.+' cannot transition/i.test(part));

  if (!details.length) return message;

  return details.map((detail, index) => (
    <React.Fragment key={index}>
      {index > 0 ? <br /> : null}
      {detail}
    </React.Fragment>
  ));
}

function PriorityCountBadge({ count, dropState }: { count: number; dropState: VideoProjectKanbanColumnDropState }) {
  return (
    <Badge
      size="small"
      className={cn(
        "gap-1 font-bold",
        dropState === "invalid"
          ? "border-transparent bg-slate-200 text-slate-400"
          : count > 0
          ? "border-transparent bg-sky-500 text-white shadow-sm hover:bg-sky-500"
          : "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-50"
      )}
      title={`${count} priority`}
      aria-label={`${count} priority ${count === 1 ? "project" : "projects"}`}
    >
      <Flag className="size-3 fill-current" aria-hidden="true" />
      {count}
    </Badge>
  );
}

function getColumnDropState(
  columnId: KanbanColumnId,
  sourceColumnId: KanbanColumnId | null,
  validDropColumnIds: KanbanColumnId[],
  hoveredColumnId: KanbanColumnId | null
): VideoProjectKanbanColumnDropState {
  if (!sourceColumnId) return "idle";
  if (columnId === sourceColumnId) return "source";
  if (!validDropColumnIds.includes(columnId)) return "invalid";
  if (columnId === hoveredColumnId) return "valid-hover";
  return "valid";
}

interface StatusMutationVariables {
  projectId: number;
  event: VideoProject.Event;
  userId?: number;
  fromColumnId: KanbanColumnId;
  toColumnId: KanbanColumnId;
  skipOptimistic?: boolean;
  rollbackContext?: BoardMutationContext;
}

interface AssignmentMutationVariables {
  projectId: number;
  field: "assignee" | "qa";
  userId: number | null;
  fromUserId: number | null;
  columnId: KanbanColumnId;
}

export interface KanbanBoardStats {
  totalCount: number;
  totalPriorityCount: number;
  isInitialLoading: boolean;
  isSyncing: boolean;
}

interface EditingQueueKanbanProps {
  boardFilters: KanbanBoardFilters;
  editors: User[];
  currentUserId?: number;
  onBoardStatsChange?: (stats: KanbanBoardStats) => void;
}

export function EditingQueueKanban({
  boardFilters,
  editors,
  currentUserId,
  onBoardStatsChange,
}: EditingQueueKanbanProps) {
  const { canManageVideoProjectQueue } = useSocialCoachSession();
  const columns = React.useMemo(() => visibleKanbanColumns(canManageVideoProjectQueue), [canManageVideoProjectQueue]);
  const queryClient = useQueryClient();
  const history = useHistory();

  const openQaStudio = (project: VideoProject) => {
    history.push(editorVideoProjectDetailUrl(project.id, { qa: true }));
  };
  const [columnSnapshots, setColumnSnapshots] = React.useState<Partial<Record<KanbanColumnId, ColumnSnapshot>>>({});
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [focusedProjectId, setFocusedProjectId] = React.useState<number | null>(null);
  const [validDropColumnIds, setValidDropColumnIds] = React.useState<KanbanColumnId[]>([]);
  const [sourceColumnId, setSourceColumnId] = React.useState<KanbanColumnId | null>(null);
  const [hoveredColumnId, setHoveredColumnId] = React.useState<KanbanColumnId | null>(null);
  const [activeCardSize, setActiveCardSize] = React.useState<{ width: number; height: number } | null>(null);
  const [pendingProjectId, setPendingProjectId] = React.useState<number | null>(null);
  const [loadingMoreKey, setLoadingMoreKey] = React.useState<KanbanColumnId | null>(null);
  const dragStartSnapshotsRef = React.useRef<Partial<Record<KanbanColumnId, ColumnSnapshot>> | null>(null);
  const dragStartProjectRef = React.useRef<VideoProject | null>(null);
  const suppressFocusDismissRef = React.useRef(false);
  const skipNextQuerySyncRef = React.useRef(false);
  const prevBoardStatsRef = React.useRef<KanbanBoardStats | null>(null);
  const dropVisualLockRef = React.useRef(false);
  const dropAnimTimeoutRef = React.useRef<number | null>(null);
  const pendingApplyRef = React.useRef<(() => void) | null>(null);
  const columnFetchAbortRef = React.useRef<AbortController | null>(null);
  const columnFetchGenerationRef = React.useRef(0);
  const isDragging = activeId !== null;

  const {
    data: boardData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: kanbanBoardQueryKey(boardFilters),
    queryFn: ({ signal }) => fetchKanbanBoard(boardFilters, { signal, columns }),
    staleTime: 0,
    structuralSharing: (oldData, newData) =>
      reuseKanbanBoardData(oldData as KanbanBoardData | undefined, newData as KanbanBoardData),
  });

  React.useEffect(() => {
    if (!boardData || isDragging) return;
    if (skipNextQuerySyncRef.current) {
      skipNextQuerySyncRef.current = false;
      return;
    }
    setColumnSnapshots(current => snapshotsFromBoardData(boardData, columns, current));
  }, [boardData, columns, isDragging]);

  const isInitialLoading = isLoading && !boardData;
  const isSyncing = isFetching && !isInitialLoading;

  const projectsById = React.useMemo(() => {
    const entries = columns.flatMap(column =>
      (columnSnapshots[column.id]?.projects ?? []).map(project => [String(project.id), project] as const)
    );
    return Object.fromEntries(entries) as Record<string, VideoProject>;
  }, [columnSnapshots, columns]);

  const boardStats = React.useMemo<KanbanBoardStats>(() => {
    const totalCount = columns.reduce((sum, column) => sum + (columnSnapshots[column.id]?.totalCount ?? 0), 0);
    const totalPriorityCount = columns.reduce(
      (sum, column) => (column.id === "completed" ? sum : sum + (columnSnapshots[column.id]?.totalPriorityCount ?? 0)),
      0
    );
    return {
      totalCount,
      totalPriorityCount,
      isInitialLoading,
      isSyncing,
    };
  }, [columnSnapshots, columns, isInitialLoading, isSyncing]);

  React.useEffect(() => {
    const prev = prevBoardStatsRef.current;
    if (
      prev &&
      prev.totalCount === boardStats.totalCount &&
      prev.totalPriorityCount === boardStats.totalPriorityCount &&
      prev.isInitialLoading === boardStats.isInitialLoading &&
      prev.isSyncing === boardStats.isSyncing
    ) {
      return;
    }
    prevBoardStatsRef.current = boardStats;
    onBoardStatsChange?.(boardStats);
  }, [boardStats, onBoardStatsChange]);

  const sensors = useSensors(
    useSensor(KanbanPointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const abortColumnFetches = () => {
    if (columnFetchAbortRef.current) {
      columnFetchAbortRef.current.abort();
      columnFetchAbortRef.current = null;
    }
  };

  const cancelBoardQuery = () => queryClient.cancelQueries({ queryKey: kanbanBoardQueryKey(boardFilters) });

  const cancelColumnNetworkRefreshes = () => {
    columnFetchGenerationRef.current += 1;
    pendingApplyRef.current = null;
    abortColumnFetches();
    return cancelBoardQuery();
  };

  const beginColumnNetworkRequest = () => {
    columnFetchGenerationRef.current += 1;
    abortColumnFetches();
    const controller = new AbortController();
    columnFetchAbortRef.current = controller;
    return controller;
  };

  const applyBoardData = (board: KanbanBoardData) => {
    const currentBoard = queryClient.getQueryData<KanbanBoardData>(kanbanBoardQueryKey(boardFilters));
    if (board === currentBoard) {
      return;
    }
    skipNextQuerySyncRef.current = true;
    const nextBoard = setKanbanBoardQueryData(queryClient, boardFilters, board) ?? board;
    setColumnSnapshots(current => snapshotsFromBoardData(nextBoard, columns, current));
  };

  const rollbackBoard = (context?: BoardMutationContext) => {
    if (context?.previousBoardData) {
      applyBoardData(context.previousBoardData);
    } else if (context?.previousSnapshots) {
      setColumnSnapshots(context.previousSnapshots);
    }
  };

  const applyProjectToBoard = (project: VideoProject, previousStatus?: VideoProject.Status | string | null) => {
    const latestBoard = queryClient.getQueryData<KanbanBoardData>(kanbanBoardQueryKey(boardFilters));
    if (!latestBoard) return;

    applyBoardData(
      applyKanbanVideoProject(latestBoard, project, currentUserId ?? 0, {
        filters: boardFilters,
        previousStatus:
          previousStatus && !canManageVideoProjectQueue ? VideoProject.clientStatus(previousStatus) : previousStatus,
      })
    );
  };

  const refreshVideoProject = async (event: EditorQueueVideoProjectChanged) => {
    const currentBoard = queryClient.getQueryData<KanbanBoardData>(kanbanBoardQueryKey(boardFilters));
    if (!currentBoard) return;

    if (event.archived) {
      if (dragStartSnapshotsRef.current) return;
      const sourceStatus = event.fromStatus ?? event.toStatus;
      applyBoardData(
        removeProjectFromKanbanBoardData(
          currentBoard,
          event.videoProjectId,
          columnIdForStatus(
            sourceStatus && !canManageVideoProjectQueue ? VideoProject.clientStatus(sourceStatus) : sourceStatus
          )
        )
      );
      return;
    }

    try {
      const project = await fetchKanbanVideoProject(event.videoProjectId);
      if (dragStartSnapshotsRef.current) return;
      applyProjectToBoard(project, event.fromStatus);
    } catch {
      // Keep the current board if the single-project fetch fails.
    }
  };

  const refreshAffectedColumns = async (columnIds: KanbanColumnId[]) => {
    const controller = beginColumnNetworkRequest();
    const generation = columnFetchGenerationRef.current;
    const currentBoard = queryClient.getQueryData<KanbanBoardData>(kanbanBoardQueryKey(boardFilters));
    if (!currentBoard) return;

    try {
      const nextBoard = await refreshKanbanColumns(boardFilters, currentBoard, columnIds, controller.signal);
      if (
        controller.signal.aborted ||
        columnFetchAbortRef.current !== controller ||
        generation !== columnFetchGenerationRef.current
      ) {
        return;
      }
      if (dragStartSnapshotsRef.current) {
        return;
      }
      applyBoardData(nextBoard);
    } catch (error) {
      if (isKanbanColumnFetchAborted(error, controller.signal) || generation !== columnFetchGenerationRef.current) {
        return;
      }
      // Keep optimistic state if background refresh fails.
    } finally {
      if (columnFetchAbortRef.current === controller) {
        columnFetchAbortRef.current = null;
      }
    }
  };

  useEditorQueueCable({
    paused: isDragging,
    pendingProjectId,
    onEvent: event => {
      if (event.type === EDITOR_QUEUE_VIDEO_PROJECT_CHANGED) {
        void refreshVideoProject(event);
        return;
      }
      void refreshAffectedColumns(
        columnIdsForEditorQueueEvent(event, { revealInternalStatuses: canManageVideoProjectQueue })
      );
    },
    onReconnect: () => {
      void refreshAffectedColumns(columns.map(column => column.id));
    },
  });

  const applyOptimisticStatusMove = async (variables: StatusMutationVariables): Promise<BoardMutationContext> => {
    abortColumnFetches();
    await cancelBoardQuery();
    setPendingProjectId(variables.projectId);

    const previousBoardData = queryClient.getQueryData<KanbanBoardData>(kanbanBoardQueryKey(boardFilters));
    const previousSnapshots = dragStartSnapshotsRef.current ?? columnSnapshots;
    const optimisticStatus = getRepresentativeStatusForColumn(variables.toColumnId);

    if (previousBoardData) {
      const sourceProject = previousBoardData[variables.fromColumnId]?.projects.find(
        item => item.id === variables.projectId
      );
      const viewerId = currentUserId ?? 0;
      const actorId = variables.userId ?? viewerId;
      const optimisticPatch = sourceProject
        ? buildOptimisticKanbanProjectPatch(sourceProject, {
            status: optimisticStatus,
            event: variables.event,
            currentUserId: viewerId,
            assignedUserId: actorId,
          })
        : {
            status: optimisticStatus,
            statusLabel: VideoProject.StatusMeta[optimisticStatus]?.label,
          };
      applyBoardData(
        moveProjectInKanbanBoardData(
          previousBoardData,
          variables.projectId,
          variables.fromColumnId,
          variables.toColumnId,
          optimisticPatch,
          viewerId
        )
      );
    }

    return { previousBoardData, previousSnapshots };
  };

  const requestAssignedUser = async (args: {
    role: VideoProject.QueueAssignment["role"];
    project: VideoProject;
    defaultUserId: number;
  }): Promise<number | null> => {
    const confirmed = await confirmAssignSelfOnTransition({
      assigningRole: args.role,
      project: args.project,
      userId: args.defaultUserId,
      staffUsers: editors,
    });
    return confirmed ? args.defaultUserId : null;
  };

  const statusMutation = useMutation({
    mutationFn: async ({ projectId, event, userId }: StatusMutationVariables) => {
      return VideoProjectServices.update(
        projectId,
        {
          event,
          ...(event === VideoProject.Event.START_EDITING ? { editorUserId: userId } : {}),
        },
        KANBAN_EXTRA_FIELDS
      );
    },
    onMutate: async variables => {
      if (variables.skipOptimistic) {
        return variables.rollbackContext;
      }
      return applyOptimisticStatusMove(variables);
    },
    onSuccess: (project, variables) => {
      const apply = () => applyProjectToBoard(project, getRepresentativeStatusForColumn(variables.fromColumnId));
      if (dropVisualLockRef.current) {
        pendingApplyRef.current = apply;
        return;
      }
      apply();
    },
    onError: (error, variables, context) => {
      if (dropAnimTimeoutRef.current != null) {
        window.clearTimeout(dropAnimTimeoutRef.current);
        dropAnimTimeoutRef.current = null;
      }
      dropVisualLockRef.current = false;
      pendingApplyRef.current = null;
      rollbackBoard(variables.rollbackContext ?? context);
      if (isDragging) resetDragState();
      focusDroppedCard(variables.projectId);
      toast.error("Failed to update project status", {
        description: formatKanbanTransitionError(error),
      });
    },
    onSettled: () => {
      setPendingProjectId(null);
    },
  });

  const assignmentMutation = useMutation({
    mutationFn: async ({ projectId, field, userId, fromUserId }: AssignmentMutationVariables) => {
      return VideoProjectServices.update(
        projectId,
        {
          event: VideoProject.Event.ASSIGN,
          role: field === "assignee" ? "EDITOR" : "QA",
          fromUserId,
          toUserId: userId,
        },
        KANBAN_EXTRA_FIELDS
      );
    },
    onMutate: async ({ projectId, field, userId }) => {
      await cancelColumnNetworkRefreshes();
      setPendingProjectId(projectId);

      const previousBoardData = queryClient.getQueryData<KanbanBoardData>(kanbanBoardQueryKey(boardFilters));
      const previousSnapshots = columnSnapshots;

      if (previousBoardData) {
        applyBoardData(
          updateProjectInKanbanBoardData(
            previousBoardData,
            projectId,
            field === "assignee" ? { editorUserId: userId } : { qaUserId: userId },
            currentUserId ?? 0
          )
        );
      }

      return { previousBoardData, previousSnapshots };
    },
    onError: (_error, _variables, context) => {
      rollbackBoard(context);
      toast.error(getErrorMessage(_error) || "Failed to update assignment");
    },
    onSettled: async (_data, _error, variables) => {
      setPendingProjectId(null);
      await refreshAffectedColumns([variables.columnId]);
    },
  });

  const resetDragState = () => {
    skipNextQuerySyncRef.current = true;
    setActiveId(null);
    setValidDropColumnIds([]);
    setSourceColumnId(null);
    setHoveredColumnId(null);
    dragStartSnapshotsRef.current = null;
    dragStartProjectRef.current = null;
  };

  const focusDroppedCard = (projectId: number, options?: { scroll?: boolean }) => {
    suppressFocusDismissRef.current = true;
    setFocusedProjectId(projectId);

    let attempts = 0;
    const reveal = () => {
      const node = document.querySelector<HTMLElement>(`[${KANBAN_PROJECT_ID_ATTR}="${projectId}"]`);
      if (!node) {
        if (attempts++ < 16) {
          requestAnimationFrame(reveal);
        } else {
          suppressFocusDismissRef.current = false;
        }
        return;
      }

      node.focus({ preventScroll: true });
      if (options?.scroll) {
        node.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
      }
      window.setTimeout(() => {
        suppressFocusDismissRef.current = false;
      }, 0);
    };

    requestAnimationFrame(reveal);
  };

  const restoreDragStartSnapshots = () => {
    if (dragStartSnapshotsRef.current) {
      setColumnSnapshots(dragStartSnapshotsRef.current);
    }
  };

  const captureBoardMutationContext = (): BoardMutationContext => ({
    previousBoardData: queryClient.getQueryData<KanbanBoardData>(kanbanBoardQueryKey(boardFilters)),
    previousSnapshots: dragStartSnapshotsRef.current ?? columnSnapshots,
  });

  const runPendingApply = () => {
    const pendingApply = pendingApplyRef.current;
    pendingApplyRef.current = null;
    pendingApply?.();
  };

  const commitDropVisuals = async (variables: StatusMutationVariables) => {
    dropAnimTimeoutRef.current = null;
    dropVisualLockRef.current = false;
    await applyOptimisticStatusMove(variables);
    resetDragState();
    runPendingApply();
    focusDroppedCard(variables.projectId, { scroll: variables.fromColumnId !== variables.toColumnId });
  };

  const scheduleDropVisualCommit = (variables: StatusMutationVariables) => {
    dropVisualLockRef.current = true;
    if (dropAnimTimeoutRef.current != null) {
      window.clearTimeout(dropAnimTimeoutRef.current);
    }
    dropAnimTimeoutRef.current = window.setTimeout(() => {
      commitDropVisuals(variables);
    }, DROP_ANIMATION_MS);
  };

  React.useEffect(
    () => () => {
      if (dropAnimTimeoutRef.current != null) {
        window.clearTimeout(dropAnimTimeoutRef.current);
      }
      columnFetchAbortRef.current?.abort();
      columnFetchAbortRef.current = null;
    },
    []
  );

  React.useEffect(() => {
    if (focusedProjectId == null || isDragging) return;

    const isInsideFocusedCard = (target: EventTarget | null) =>
      target instanceof Element && target.closest(`[${KANBAN_PROJECT_ID_ATTR}="${focusedProjectId}"]`) != null;

    const dismiss = (event: Event) => {
      if (suppressFocusDismissRef.current) return;
      if (isInsideFocusedCard(event.target)) return;
      setFocusedProjectId(null);
      const active = document.activeElement;
      if (active instanceof HTMLElement && isInsideFocusedCard(active)) {
        active.blur();
      }
    };

    document.addEventListener("pointerdown", dismiss, true);
    document.addEventListener("focusin", dismiss);
    return () => {
      document.removeEventListener("pointerdown", dismiss, true);
      document.removeEventListener("focusin", dismiss);
    };
  }, [focusedProjectId, isDragging]);

  const commitStatusMutation = (next: StatusMutationVariables, endDrag = false) => {
    if (!endDrag) {
      statusMutation.mutate(next);
      return;
    }

    statusMutation.mutate({
      ...next,
      skipOptimistic: true,
      rollbackContext: captureBoardMutationContext(),
    });
    scheduleDropVisualCommit(next);
  };

  const finalizeStatusChange = async (
    project: VideoProject,
    variables: StatusMutationVariables,
    options?: { endDrag?: boolean }
  ) => {
    void cancelColumnNetworkRefreshes();
    const role = assigningRoleForEvent(variables.event);
    const existingUserId = role ? assignedUserIdForRole(project, role) : null;
    const defaultUserId = variables.userId ?? currentUserId;

    if (role) {
      if (existingUserId != null) {
        commitStatusMutation({ ...variables, userId: existingUserId }, options?.endDrag);
        return;
      }

      if (defaultUserId == null) {
        if (options?.endDrag) {
          restoreDragStartSnapshots();
          resetDragState();
          focusDroppedCard(project.id);
        }
        toast.error("Cannot move project to this status");
        return;
      }

      if (options?.endDrag) {
        dropVisualLockRef.current = true;
        if (dropAnimTimeoutRef.current != null) {
          window.clearTimeout(dropAnimTimeoutRef.current);
        }
        dropAnimTimeoutRef.current = window.setTimeout(async () => {
          dropAnimTimeoutRef.current = null;
          dropVisualLockRef.current = false;
          const context = await applyOptimisticStatusMove({ ...variables, userId: defaultUserId });
          resetDragState();
          focusDroppedCard(variables.projectId, { scroll: variables.fromColumnId !== variables.toColumnId });
          const selectedUserId = await requestAssignedUser({
            role,
            project,
            defaultUserId,
          });
          if (selectedUserId == null) {
            rollbackBoard(context);
            setPendingProjectId(null);
            focusDroppedCard(project.id);
            return;
          }
          statusMutation.mutate({
            ...variables,
            userId: selectedUserId,
            skipOptimistic: true,
            rollbackContext: context,
          });
        }, DROP_ANIMATION_MS);
        return;
      }

      const context = await applyOptimisticStatusMove({ ...variables, userId: defaultUserId });
      const selectedUserId = await requestAssignedUser({
        role,
        project,
        defaultUserId,
      });
      if (selectedUserId == null) {
        rollbackBoard(context);
        setPendingProjectId(null);
        return;
      }

      statusMutation.mutate({
        ...variables,
        userId: selectedUserId,
        skipOptimistic: true,
        rollbackContext: context,
      });
      return;
    }

    commitStatusMutation(variables, options?.endDrag);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const draggedId = String(event.active.id);
    const project = projectsById[draggedId];
    if (!project) return;

    const fromColumn = getKanbanColumnId(project.status);
    if (!fromColumn) return;

    const initialRect = event.active.rect.current.initial;
    const fallbackRect =
      event.activatorEvent.target instanceof Element
        ? event.activatorEvent.target.closest("[data-kanban-card]")?.getBoundingClientRect()
        : null;
    const measuredRect = initialRect ?? fallbackRect;
    dragStartSnapshotsRef.current = columnSnapshots;
    dragStartProjectRef.current = project;
    void cancelColumnNetworkRefreshes();
    setActiveCardSize(measuredRect ? { width: measuredRect.width, height: measuredRect.height } : null);
    setActiveId(draggedId);
    setFocusedProjectId(null);
    setSourceColumnId(fromColumn);
    setHoveredColumnId(fromColumn);
    setValidDropColumnIds(
      getValidStatusColumnIds(project.status, {
        requiresQa: project.requiresQa,
        currentUserId,
      })
    );
  };

  const handleDragOver = (event: DragOverEvent) => {
    const project = dragStartProjectRef.current ?? projectsById[String(event.active.id)];
    const startSnapshots = dragStartSnapshotsRef.current;
    const fromColumn = project ? getKanbanColumnId(project.status) : null;
    if (!project || !fromColumn || !startSnapshots) return;

    const hoverColumnId = event.over ? findContainer(String(event.over.id), startSnapshots) : null;
    if (!hoverColumnId) return;

    setHoveredColumnId(current => (current === hoverColumnId ? current : hoverColumnId));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const project = dragStartProjectRef.current ?? projectsById[String(active.id)];
    const fromColumnId = sourceColumnId;
    const overContainer = over ? findContainer(String(over.id), columnSnapshots) : null;
    const dropRequest =
      project && overContainer && fromColumnId && fromColumnId !== overContainer
        ? getVideoProjectEventForStatusChange(project.status, overContainer, {
            requiresQa: project.requiresQa,
            currentUserId,
          })
        : null;

    if (over && project && pendingProjectId === null && fromColumnId && overContainer && dropRequest) {
      const variables: StatusMutationVariables = {
        projectId: project.id,
        event: dropRequest.event,
        userId: dropRequest.userId ?? currentUserId,
        fromColumnId,
        toColumnId: overContainer,
      };

      if (isQaResolutionEvent(dropRequest.event)) {
        restoreDragStartSnapshots();
        resetDragState();
        openQaStudio(project);
        return;
      }

      void finalizeStatusChange(project, variables, { endDrag: true });
      return;
    }

    restoreDragStartSnapshots();
    if (over && project && fromColumnId && overContainer && fromColumnId !== overContainer && !dropRequest) {
      toast.error("Cannot move project to this column");
    }
    const droppedId = project?.id;
    resetDragState();
    if (droppedId != null) focusDroppedCard(droppedId);
  };

  const handleLoadMore = React.useCallback(
    async (columnId: KanbanColumnId) => {
      const column = columns.find(item => item.id === columnId);
      const currentBoard = queryClient.getQueryData<KanbanBoardData>(kanbanBoardQueryKey(boardFilters));
      const columnData = currentBoard?.[columnId];

      if (!column || !currentBoard || !columnData || !columnData.hasMore || loadingMoreKey !== null) {
        return;
      }

      setLoadingMoreKey(columnId);
      const controller = beginColumnNetworkRequest();
      const generation = columnFetchGenerationRef.current;
      try {
        const page = await fetchNextKanbanColumnPage(column, boardFilters, columnData, controller.signal);
        if (
          controller.signal.aborted ||
          columnFetchAbortRef.current !== controller ||
          generation !== columnFetchGenerationRef.current
        ) {
          return;
        }
        applyBoardData(appendKanbanColumnPage(currentBoard, columnId, page));
      } catch (error) {
        if (isKanbanColumnFetchAborted(error, controller.signal)) {
          return;
        }
        toast.error("Failed to load more projects");
      } finally {
        if (columnFetchAbortRef.current === controller) {
          columnFetchAbortRef.current = null;
        }
        setLoadingMoreKey(null);
      }
    },
    [boardFilters, columns, loadingMoreKey, queryClient]
  );

  const mutateAssignment = React.useCallback(
    (project: VideoProject, field: "assignee" | "qa", userId: number | null) => {
      const currentId = field === "assignee" ? project.editorUserId : project.qaUserId;
      if ((userId ?? null) === (currentId ?? null)) return;

      const role = field === "assignee" ? "EDITOR" : "QA";
      if (userId == null && !canUnassignStaff(role, project.status)) {
        toast.error(
          field === "assignee"
            ? "Editor can't be unassigned after editing has started"
            : "QA can't be unassigned after QA review has started"
        );
        return;
      }

      const columnId = getKanbanColumnId(project.status);
      if (!columnId || pendingProjectId !== null) return;

      assignmentMutation.mutate({
        projectId: project.id,
        field,
        userId,
        fromUserId: currentId ?? null,
        columnId,
      });
    },
    [assignmentMutation, pendingProjectId]
  );

  const mutateStatus = React.useCallback(
    async (project: VideoProject, toColumnId: KanbanColumnId) => {
      const fromColumnId = getKanbanColumnId(project.status);
      if (!fromColumnId || fromColumnId === toColumnId || pendingProjectId !== null) return;

      const dropRequest = getVideoProjectEventForStatusChange(project.status, toColumnId, {
        requiresQa: project.requiresQa,
        currentUserId,
      });

      if (!dropRequest) {
        toast.error("Cannot move project to this status");
        return;
      }

      if (isQaResolutionEvent(dropRequest.event)) {
        openQaStudio(project);
        return;
      }

      await finalizeStatusChange(project, {
        projectId: project.id,
        event: dropRequest.event,
        userId: dropRequest.userId ?? currentUserId,
        fromColumnId,
        toColumnId,
      });
    },
    [currentUserId, finalizeStatusChange, pendingProjectId]
  );

  const mutateAssignmentRef = React.useRef(mutateAssignment);
  mutateAssignmentRef.current = mutateAssignment;
  const mutateStatusRef = React.useRef(mutateStatus);
  mutateStatusRef.current = mutateStatus;
  const handleLoadMoreRef = React.useRef(handleLoadMore);
  handleLoadMoreRef.current = handleLoadMore;

  const isCardDraggable = React.useCallback((project: VideoProject) => isKanbanCardDraggable(project.status), []);
  const onAssigneeChange = React.useCallback((project: VideoProject, userId: number | null) => {
    mutateAssignmentRef.current(project, "assignee", userId);
  }, []);
  const onQaChange = React.useCallback((project: VideoProject, userId: number | null) => {
    mutateAssignmentRef.current(project, "qa", userId);
  }, []);
  const onStatusChange = React.useCallback((project: VideoProject, columnId: KanbanColumnId) => {
    void mutateStatusRef.current(project, columnId);
  }, []);
  const onLoadMore = React.useCallback((columnId: KanbanColumnId) => {
    void handleLoadMoreRef.current(columnId);
  }, []);

  const dropPlaceholder = React.useMemo(() => {
    if (!isDragging || !hoveredColumnId || !sourceColumnId || hoveredColumnId === sourceColumnId) return null;
    const project = dragStartProjectRef.current;
    const startSnapshots = dragStartSnapshotsRef.current;
    if (!project || !startSnapshots) return null;
    const index = getKanbanDropPlaceholderIndex({
      startSnapshots,
      project,
      sourceColumnId,
      hoverColumnId: hoveredColumnId,
      validDropColumnIds,
      currentUserId,
    });
    if (index == null) return null;
    return { columnId: hoveredColumnId, index };
  }, [currentUserId, hoveredColumnId, isDragging, sourceColumnId, validDropColumnIds]);

  const activeProject = activeId ? projectsById[activeId] : null;
  const overlayProjectRef = React.useRef<VideoProject | null>(null);
  if (activeProject) overlayProjectRef.current = activeProject;
  const dragOverlayProject = activeProject ?? overlayProjectRef.current;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      autoScroll={{ layoutShiftCompensation: false }}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        const droppedId = dragStartProjectRef.current?.id;
        restoreDragStartSnapshots();
        resetDragState();
        if (droppedId != null) focusDroppedCard(droppedId);
      }}
    >
      <VideoProjectKanbanBoard
        columns={columns}
        droppable
        hideEmpty={isDragging}
        cardSkeleton="staff"
        loadingMoreColumnId={loadingMoreKey}
        onLoadMore={onLoadMore}
        getColumnView={column => {
          const snapshot = columnSnapshots[column.id];
          const columnData = boardData?.[column.id];
          const dropState = getColumnDropState(column.id, sourceColumnId, validDropColumnIds, hoveredColumnId);
          const isDropTarget = dropPlaceholder?.columnId === column.id;
          const showPriority = column.id !== "completed";

          return {
            projects: snapshot?.projects ?? EMPTY_PROJECTS,
            totalCount: snapshot?.totalCount ?? 0,
            hasMore: columnData?.hasMore ?? false,
            loading: isInitialLoading || !snapshot,
            showCount: !boardFilters.priority || column.id === "completed",
            dropState,
            dropPlaceholderIndex: isDropTarget ? dropPlaceholder.index : null,
            dropPlaceholderHeight: isDropTarget ? activeCardSize?.height : undefined,
            headerAccessoryLoading: showPriority ? <Skeleton className="h-[22px] w-9 rounded-full" /> : null,
            headerAccessory: showPriority ? (
              <PriorityCountBadge count={snapshot?.totalPriorityCount ?? 0} dropState={dropState} />
            ) : null,
          };
        }}
        renderCard={project => (
          <KanbanCard
            videoProject={project}
            staffUsers={editors}
            currentUserId={currentUserId}
            disabled={pendingProjectId === project.id}
            draggable={isCardDraggable(project) && pendingProjectId !== project.id}
            collapseInPlace={dropPlaceholder != null && activeId === String(project.id)}
            isDragSource={activeId === String(project.id)}
            selected={!isDragging && focusedProjectId === project.id}
            onAssigneeChange={onAssigneeChange}
            onQaChange={onQaChange}
            onStatusChange={onStatusChange}
          />
        )}
      />
      <DragOverlay
        dropAnimation={DROP_ANIMATION}
        zIndex={9999}
        style={activeCardSize ? { width: activeCardSize.width } : undefined}
      >
        {dragOverlayProject ? <KanbanCardOverlay videoProject={dragOverlayProject} staffUsers={editors} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
